import { usageCaptureProvenanceSchema } from '../onboarding-sessions/usage-config';
import { z } from 'zod';
import { isCalendarDate } from '../check-ins/schema';

// ─── member baseline vocabulary ─────────────────────────────────────────────
//
// Governed enums for the native baseline model (`member_baselines` and
// `member_baseline_entries`). The `as const` arrays drive BOTH the text+CHECK
// constraints rendered in the DB schema and every wire schema (ADR-056 Rule 8);
// a native pgEnum is banned there and by ADR-105 Rule 2. Row shapes are not
// mirrored here: readers consume `typeof memberBaselines.$inferSelect`
// (ADR-066). What this file adds beyond the vocabularies is the canonical-week
// VIEW every reader shares, so billing, voice and Atlas cannot each re-derive
// "which seven days" from raw rows.

/**
 * What a baseline describes. `reported_week` is the dated week before capture
 * (onboarding `day_1..day_7`). `typical_week` is a dateless usual week, given
 * when the member said the reported week was not typical; its entries carry a
 * weekday instead of a date. `legacy_window` is a dated 1.0 window of up to 30
 * days, imported at migration.
 */
export const BASELINE_KIND = ['reported_week', 'typical_week', 'legacy_window'] as const;
export const baselineKindSchema = z.enum(BASELINE_KIND);
export type BaselineKind = z.infer<typeof baselineKindSchema>;

/** Where a baseline was captured: native onboarding, or the Pelago 1.0 import. */
export const BASELINE_SOURCE = ['onboarding', 'legacy_1_0'] as const;
export const baselineSourceSchema = z.enum(BASELINE_SOURCE);
export type BaselineSource = z.infer<typeof baselineSourceSchema>;

/**
 * Whether the baseline is known to describe the member's usual use.
 * `confirmed`: the member said so (or gave a typical week). `not_representative`:
 * the member said the reported week was unusual. `unknown`: nobody asked, which
 * is every 1.0 window. Only `confirmed` may drive a clinical or billing
 * decision that depends on the week being usual.
 */
export const BASELINE_REPRESENTATIVENESS = ['confirmed', 'not_representative', 'unknown'] as const;
export const baselineRepresentativenessSchema = z.enum(BASELINE_REPRESENTATIVENESS);
export type BaselineRepresentativeness = z.infer<typeof baselineRepresentativenessSchema>;

/**
 * The unit an entry's `quantity` counts in. Units are never converted into one
 * another and never summed across: a 1.0 cannabis "standard unit" is kept apart
 * from the Galapagos "block" because nobody has established they measure the
 * same thing. A new unit is added as its own value. `use` has no standard
 * measure: the quantity is the member's own count when they gave one, and NULL
 * when the source records use only (1.0 opioid and stimulant rows).
 */
export const BASELINE_UNIT = [
  'standard_drink',
  'cannabis_block',
  'cannabis_standard_unit_1_0',
  'use',
] as const;
export const baselineUnitSchema = z.enum(BASELINE_UNIT);
export type BaselineUnit = z.infer<typeof baselineUnitSchema>;

/** ISO weekday, Monday = 1 … Sunday = 7. */
export const baselineWeekdaySchema = z.number().int().min(1).max(7);

/** Position in the canonical week: 1 is the most recent day. */
export const baselineWindowDaySchema = z.number().int().min(1).max(7);

/** The number of days in a canonical week. */
export const BASELINE_CANONICAL_WEEK_DAYS = 7;

/** A baseline calendar day on the wire: `YYYY-MM-DD`, never an instant (ADR-095). */
export const baselineCalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD date')
  .refine(isCalendarDate, 'must be a real calendar date');

/** Longest substance-type label accepted into `details.types`. */
export const BASELINE_DETAIL_TYPE_MAX_LENGTH = 200;
/** Most substance-type labels accepted on one entry. */
export const BASELINE_DETAIL_TYPES_MAX = 50;

/**
 * Typed `details` for an entry, discriminated by `kind` so a future detail
 * shape is a new member rather than a loosened one. `substance_types` carries
 * the 1.0 opioid and stimulant type lists as delivered. `usage_capture` keeps
 * an explicit native yes/no capture distinct from legacy counted entries.
 */
export const baselineSubstanceTypesDetailsSchema = z.object({
  kind: z.literal('substance_types'),
  types: z
    .array(z.string().min(1).max(BASELINE_DETAIL_TYPE_MAX_LENGTH))
    .max(BASELINE_DETAIL_TYPES_MAX),
});
const baselineBinaryCaptureSchema = usageCaptureProvenanceSchema.extend({
  captureMode: z.literal('yes-no'),
});
export const baselineUsageCaptureDetailsSchema = z.object({
  kind: z.literal('usage_capture'),
  capture: baselineBinaryCaptureSchema,
});
// Storage details include capture provenance. The public canonical entry below
// projects only substance types, so an older client never sees a new details kind.
export const baselineEntryDetailsSchema = z.discriminatedUnion('kind', [
  baselineSubstanceTypesDetailsSchema,
  baselineUsageCaptureDetailsSchema,
]);
export type BaselineEntryDetails = z.infer<typeof baselineEntryDetailsSchema>;

// ─── the canonical week (read view) ─────────────────────────────────────────
//
// These are RESPONSE shapes a client (Atlas) parses, so none is `.strict()`
// (ADR-135 Rule 1).

/** One day of a canonical week. Exactly one of `entryDate` / `weekday` is set. */
export const canonicalWeekEntrySchema = z.object({
  windowDay: baselineWindowDaySchema,
  entryDate: baselineCalendarDateSchema.nullable(),
  weekday: baselineWeekdaySchema.nullable(),
  used: z.boolean(),
  quantity: z.number().nonnegative().nullable(),
  details: baselineSubstanceTypesDetailsSchema.nullable(),
});
export type CanonicalWeekEntry = z.infer<typeof canonicalWeekEntrySchema>;

const canonicalWeekCoreSchema = z.object({
  baselineId: z.string(),
  /** Optional capture metadata. The existing canonical response uses non-strict objects; installed parsers strip this new key (ADR-135 Rule 2). */
  capture: baselineBinaryCaptureSchema.optional(),
  kind: baselineKindSchema,
  source: baselineSourceSchema,
  representativeness: baselineRepresentativenessSchema,
  capturedOn: baselineCalendarDateSchema,
  /** The one unit every entry of this week counts in; null when it has no entries. */
  unit: baselineUnitSchema.nullable(),
  /** Seven `window_day` entries are present. */
  complete: z.boolean(),
  /** Complete, and every entry is unused. */
  allUnused: z.boolean(),
  /** The `window_day` entries, most recent (`windowDay = 1`) first. */
  entries: z.array(canonicalWeekEntrySchema).max(BASELINE_CANONICAL_WEEK_DAYS),
});

/**
 * The canonical week of a member's designated baseline for one programme.
 * When the designated baseline is a `typical_week`, `reportedWeek` carries the
 * not-designated reported week from the same capture, for display only.
 */
export const canonicalWeekSchema = canonicalWeekCoreSchema.extend({
  reportedWeek: canonicalWeekCoreSchema.nullable(),
});
export type CanonicalWeek = z.infer<typeof canonicalWeekSchema>;
export type CanonicalWeekCore = z.infer<typeof canonicalWeekCoreSchema>;

/** The intake record's `baseline` section: the designated canonical week, or null. */
export const intakeBaselineSchema = canonicalWeekSchema;
export type IntakeBaseline = z.infer<typeof intakeBaselineSchema>;
